import React, { useEffect, useMemo, useRef, useState } from "react";
import { alpha, styled, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Avatar from "@mui/material/Avatar";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import BottomNavigation from "@mui/material/BottomNavigation";
import BottomNavigationAction from "@mui/material/BottomNavigationAction";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import MuiDrawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Popover from "@mui/material/Popover";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import AppsRoundedIcon from "@mui/icons-material/AppsRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import DarkModeRoundedIcon from "@mui/icons-material/DarkModeRounded";
import LightModeRoundedIcon from "@mui/icons-material/LightModeRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import NotificationsNoneRoundedIcon from "@mui/icons-material/NotificationsNoneRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";

import { Logo } from "@components/ui/Layout/Menu/Logo";
import {
  MenuIcon,
  SectionSeparator,
  getSeparatorTitle,
  hasChildren,
  hasModules,
  isSeparator,
  readNotificationIcon,
  toAbsoluteUrl,
} from "./menu.utils";
import { useThemeMode } from "@hooks/ThemeMode";

const DRAWER_OPEN_WIDTH = 280;
const DRAWER_CLOSED_WIDTH = 60;
const MOBILE_BREAKPOINT = 1100;
const MOBILE_TOP_BAR_HEIGHT = 0;
const MOBILE_BOTTOM_BAR_HEIGHT = 58;
const MOBILE_DRAWER_Z_INDEX = 130;
const MOBILE_CHROME_Z_INDEX = 141;
const MENU_RADIUS = 0;
const MENU_DESKTOP_STATE_KEY = "mt-user:menu:desktop-open";
const COLLAPSED_FLYOUT_GAP = 10;
const EMPTY_COLLAPSED_FLYOUT = { anchorEl: null, item: null, anchorPosition: null };
const EMPTY_COLLAPSED_HINT = { label: "", position: null };

const openedMixin = (theme) => ({
  width: DRAWER_OPEN_WIDTH,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  width: DRAWER_CLOSED_WIDTH,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
});

const DesktopDrawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: DRAWER_OPEN_WIDTH,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

function normalizeBool(value) {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "number") {
    return value === 1;
  }
  const parsed = String(value || "").toLowerCase();
  return parsed === "1" || parsed === "true" || parsed === "yes";
}

function openHref(href) {
  if (!href || href === "#") {
    return;
  }
  window.location.href = href;
}

function normalizeSearchToken(value) {
  return String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
}

function findMenuUrl(menuItems, hostBase, matcher) {
  if (!Array.isArray(menuItems) || typeof matcher !== "function") {
    return "#";
  }

  for (const app of menuItems) {
    if (!app || isSeparator(app)) {
      continue;
    }
    const appName = normalizeSearchToken(app.name);
    const appCode = normalizeSearchToken(app.code);
    if (matcher({ level: "app", name: appName, code: appCode })) {
      return toAbsoluteUrl(app.url || "#", hostBase);
    }

    const modules = Array.isArray(app.modules) ? app.modules : [];
    for (const module of modules) {
      if (!module || isSeparator(module)) {
        continue;
      }
      const moduleName = normalizeSearchToken(module.name);
      const moduleCode = normalizeSearchToken(module.code);
      if (matcher({ level: "module", name: moduleName, code: moduleCode })) {
        return toAbsoluteUrl(module.url || "#", hostBase);
      }

      const children = Array.isArray(module.children) ? module.children : [];
      for (const child of children) {
        if (!child || isSeparator(child)) {
          continue;
        }
        const childName = normalizeSearchToken(child.name);
        const childCode = normalizeSearchToken(child.code);
        if (matcher({ level: "child", name: childName, code: childCode })) {
          return toAbsoluteUrl(child.url || "#", hostBase);
        }
      }
    }
  }

  return "#";
}

export default function MenuShell({
  payload,
  hostBase = "",
  onLayoutStateChange = null,
  onNotificationsClick = null,
}) {
  const theme = useTheme();
  const { mode, toggleMode } = useThemeMode();
  const isMobile = useMediaQuery(`(max-width:${MOBILE_BREAKPOINT}px)`);
  const shellRootRef = useRef(null);
  const currentMode = mode || theme.palette.mode;
  const isDarkMode = currentMode === "dark";

  const menuPalette = theme.palette.menu || {
    bg: theme.palette.primary.main,
    text: theme.palette.primary.contrastText,
    icon: theme.palette.secondary.main,
    panelBg: theme.palette.background.paper,
    panelText: theme.palette.text.primary,
    border: theme.palette.divider,
    buttonBg: theme.palette.background.paper,
    buttonText: theme.palette.text.secondary,
    buttonHoverBg: theme.palette.secondary.dark,
    buttonHoverText: theme.palette.secondary.contrastText,
    buttonActiveBg: theme.palette.secondary.main,
    buttonActiveText: theme.palette.secondary.contrastText,
    itemHoverBg: alpha(theme.palette.text.primary, 0.08),
    itemActiveBg: alpha(theme.palette.secondary.main, 0.18),
    itemActiveText: theme.palette.secondary.main,
    badgeBg: theme.palette.warning.main,
    badgeText: theme.palette.warning.contrastText,
  };

  const menuItems = Array.isArray(payload?.menu) ? payload.menu : [];
  const notifications = payload?.notifications || {
    total: 0,
    unread_total: 0,
    status_text: "Todo al dia",
    items: [],
  };

  const defaultSection = useMemo(() => {
    const firstIndex = menuItems.findIndex((item) => item && !isSeparator(item));
    return String(firstIndex >= 0 ? firstIndex : 0);
  }, [menuItems]);

  const firstMenuLink = useMemo(() => {
    const firstApp = menuItems.find((item) => item && !isSeparator(item));
    return toAbsoluteUrl(firstApp?.url || "#", hostBase);
  }, [menuItems, hostBase]);
  const quickLinks = useMemo(() => ({
    inicio: firstMenuLink,
    preventa: findMenuUrl(
      menuItems,
      hostBase,
      ({ name, code }) =>
        name.includes("preventa") ||
        name.includes("pregunta") ||
        code.includes("questions_answers") ||
        code === "q"
    ),
    postventa: findMenuUrl(
      menuItems,
      hostBase,
      ({ name, code }) =>
        name.includes("post venta") ||
        name.includes("postventa") ||
        code.includes("post_sale") ||
        code.includes("post-sale")
    ),
    perfil: toAbsoluteUrl(payload?.perfilUrl || "#", hostBase),
  }), [firstMenuLink, hostBase, menuItems, payload?.perfilUrl]);
  const [currentPath, setCurrentPath] = useState(() =>
    typeof window !== "undefined" ? window.location.pathname : ""
  );

  const [desktopOpen, setDesktopOpen] = useState(() => {
    if (typeof window === "undefined") {
      return true;
    }
    const stored = window.localStorage.getItem(MENU_DESKTOP_STATE_KEY);
    if (stored === "0" || stored === "false") {
      return false;
    }
    if (stored === "1" || stored === "true") {
      return true;
    }
    return true;
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [panelSection, setPanelSection] = useState("apps");
  const [activeApp, setActiveApp] = useState(defaultSection);
  const [expandedMap, setExpandedMap] = useState({});
  const [userAnchor, setUserAnchor] = useState(null);
  const [collapsedFlyout, setCollapsedFlyout] = useState(EMPTY_COLLAPSED_FLYOUT);
  const [collapsedHint, setCollapsedHint] = useState(EMPTY_COLLAPSED_HINT);
  const collapsedHintTimersRef = useRef({ show: null, hide: null });
  const [dialog, setDialog] = useState({ open: false, iframe: false, title: "", url: "" });

  useEffect(() => {
    if (typeof onLayoutStateChange === "function") {
      onLayoutStateChange({
        isMobile,
        desktopOpen,
        mobileOpen,
      });
    }
  }, [desktopOpen, isMobile, mobileOpen, onLayoutStateChange]);

  useEffect(() => {
    if (!menuItems[Number(activeApp)] || isSeparator(menuItems[Number(activeApp)])) {
      setActiveApp(defaultSection);
    }
  }, [activeApp, defaultSection, menuItems]);

  useEffect(() => {
    if (!isMobile) {
      setMobileOpen(false);
      setPanelSection("apps");
    }
  }, [isMobile]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }
    const syncPath = () => {
      setCurrentPath(window.location.pathname || "");
    };
    window.addEventListener("popstate", syncPath);
    window.addEventListener("hashchange", syncPath);
    return () => {
      window.removeEventListener("popstate", syncPath);
      window.removeEventListener("hashchange", syncPath);
    };
  }, []);

  useEffect(() => {
    if (desktopOpen) {
      setCollapsedFlyout(EMPTY_COLLAPSED_FLYOUT);
      setCollapsedHint(EMPTY_COLLAPSED_HINT);
    }
  }, [desktopOpen]);

  useEffect(() => () => {
    const timers = collapsedHintTimersRef.current;
    if (timers.show) {
      window.clearTimeout(timers.show);
    }
    if (timers.hide) {
      window.clearTimeout(timers.hide);
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    window.localStorage.setItem(MENU_DESKTOP_STATE_KEY, desktopOpen ? "1" : "0");
  }, [desktopOpen]);

  const navigateSmart = (href) => {
    if (!href || href === "#") return;

    let targetUrl;
    try {
      targetUrl = new URL(href, window.location.href);
    } catch (_error) {
      openHref(href);
      return;
    }

    const root = document.getElementById("react-admin-root");
    const reactMounted = root?.dataset?.reactMounted === "1";
    const isSameOrigin = targetUrl.origin === window.location.origin;
    const isAdminPath =
      targetUrl.pathname.startsWith("/-/") || targetUrl.pathname === "/perfil";
    const canUseSpaNavigation = isSameOrigin && reactMounted && isAdminPath;

    if (canUseSpaNavigation) {
      const nextPath = `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`;
      const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      if (nextPath === currentPath) return;
      window.history.pushState({ source: "app-menu" }, "", nextPath);
      window.dispatchEvent(new PopStateEvent("popstate"));
      return;
    }

    openHref(targetUrl.toString());
  };

  const navigate = (url, options = {}) => {
    const { closeMobile = true } = options;
    setCollapsedFlyout(EMPTY_COLLAPSED_FLYOUT);
    setCollapsedHint(EMPTY_COLLAPSED_HINT);
    if (closeMobile) {
      setMobileOpen(false);
    }
    navigateSmart(toAbsoluteUrl(url, hostBase));
  };

  const toggleExpanded = (key, siblingKeys = [], forceValue) => {
    setExpandedMap((prev) => {
      const nextOpen = typeof forceValue === "boolean" ? forceValue : !prev[key];
      const next = { ...prev };

      if (nextOpen) {
        siblingKeys.forEach((siblingKey) => {
          if (siblingKey !== key) {
            next[siblingKey] = false;
          }
        });
      }

      next[key] = nextOpen;
      return next;
    });
  };

  const openNotifications = () => {
    if (typeof onNotificationsClick === "function") {
      onNotificationsClick();
    }
    setCollapsedFlyout(EMPTY_COLLAPSED_FLYOUT);
    setCollapsedHint(EMPTY_COLLAPSED_HINT);
    setPanelSection("notifications");
    if (isMobile) {
      setMobileOpen(true);
      return;
    }
    setDesktopOpen(true);
  };

  const openApplications = () => {
    setCollapsedFlyout(EMPTY_COLLAPSED_FLYOUT);
    setCollapsedHint(EMPTY_COLLAPSED_HINT);
    setPanelSection("apps");
    if (isMobile) {
      setMobileOpen(true);
      return;
    }
    setDesktopOpen(true);
  };

  const openNotificationItem = (item) => {
    if (!item) {
      return;
    }
    const link = toAbsoluteUrl(item.link || item.Link || "#", hostBase);
    const isIframe = normalizeBool(item.is_iframe ?? item.IsIframe);
    const title = item.preview || item.Preview || "Notificacion";

    if (isIframe) {
      setDialog({
        open: true,
        iframe: true,
        title,
        url: link,
      });
      return;
    }

    window.open(link, "_blank", "noopener,noreferrer");
  };

  const closeDialog = () => {
    setDialog({ open: false, iframe: false, title: "", url: "" });
  };

  const closeCollapsedFlyout = () => {
    setCollapsedFlyout(EMPTY_COLLAPSED_FLYOUT);
  };

  const openCollapsedHint = (anchorEl, label) => {
    if (!anchorEl || !label) {
      return;
    }
    const rect = anchorEl.getBoundingClientRect();
    const position = {
      left: Math.round(rect.right + COLLAPSED_FLYOUT_GAP),
      top: Math.round(rect.top + (rect.height / 2)),
    };
    const timers = collapsedHintTimersRef.current;
    if (timers.hide) {
      window.clearTimeout(timers.hide);
      timers.hide = null;
    }
    if (timers.show) {
      window.clearTimeout(timers.show);
    }
    timers.show = window.setTimeout(() => {
      setCollapsedHint((prev) => {
        if (
          prev.label === label &&
          prev.position?.left === position.left &&
          prev.position?.top === position.top
        ) {
          return prev;
        }
        return { label, position };
      });
      timers.show = null;
    }, 70);
  };

  const closeCollapsedHint = () => {
    const timers = collapsedHintTimersRef.current;
    if (timers.show) {
      window.clearTimeout(timers.show);
      timers.show = null;
    }
    if (timers.hide) {
      window.clearTimeout(timers.hide);
    }
    timers.hide = window.setTimeout(() => {
      setCollapsedHint(EMPTY_COLLAPSED_HINT);
      timers.hide = null;
    }, 90);
  };

  const getCollapsedFlyoutAnchorPosition = (anchorEl) => {
    if (!anchorEl || typeof anchorEl.getBoundingClientRect !== "function") {
      return null;
    }
    const rect = anchorEl.getBoundingClientRect();
    return {
      top: Math.round(rect.top),
      left: Math.round(rect.right + COLLAPSED_FLYOUT_GAP),
    };
  };

  const openUserMenu = (event) => {
    setUserAnchor(event.currentTarget);
  };

  const closeUserMenu = () => {
    setUserAnchor(null);
  };

  const modeLabel = isDarkMode ? "Cambiar a modo claro" : "Cambiar a modo oscuro";
  const isNotificationsSection = panelSection === "notifications";
  const sectionToggleLabel = isNotificationsSection ? "Ver Apps" : "Ver notificaciones";
  const togglePanelSection = () => {
    if (isNotificationsSection) {
      setPanelSection("apps");
      return;
    }
    openNotifications();
  };
  const openMobileMenu = () => {
    setPanelSection("apps");
    setMobileOpen(true);
  };
  const renderModeIcon = (size = 18) =>
    isDarkMode
      ? <LightModeRoundedIcon sx={{ fontSize: size }} />
      : <DarkModeRoundedIcon sx={{ fontSize: size }} />;
  const renderSectionIcon = (size = 18) => (
    isNotificationsSection
      ? <AppsRoundedIcon sx={{ fontSize: size }} />
      : (
        <Badge
          badgeContent={notifications?.unread_total || 0}
          color="warning"
          max={99}
          overlap="circular"
          sx={{
            "& .MuiBadge-badge": {
              fontSize: 10,
              height: 16,
              minWidth: 16,
              borderRadius: MENU_RADIUS,
              bgcolor: menuPalette.badgeBg,
              color: menuPalette.badgeText,
            },
          }}
        >
          <NotificationsNoneRoundedIcon sx={{ fontSize: size }} />
        </Badge>
      )
  );
  const headerActionColor = desktopOpen ? menuPalette.panelText : menuPalette.text;
  const modeToggleSx = {
    width: 36,
    height: 36,
    borderRadius: MENU_RADIUS,
    color: headerActionColor,
    border: "1px solid",
    borderColor: alpha(headerActionColor, 0.28),
    bgcolor: alpha(headerActionColor, 0.08),
    "&:hover": {
      bgcolor: alpha(headerActionColor, 0.16),
      borderColor: alpha(headerActionColor, 0.5),
    },
  };
  const sectionToggleSx = {
    ...modeToggleSx,
    bgcolor: isNotificationsSection
      ? alpha(headerActionColor, 0.16)
      : alpha(headerActionColor, 0.08),
    borderColor: isNotificationsSection
      ? alpha(headerActionColor, 0.5)
      : alpha(headerActionColor, 0.28),
  };
  const isSamePath = (href) => {
    if (!href || href === "#") {
      return false;
    }
    try {
      const target = new URL(href, window.location.href);
      return target.origin === window.location.origin && target.pathname === currentPath;
    } catch (_error) {
      return false;
    }
  };
  const quickActionItems = [
    {
      key: "notifications",
      label: "Notificaciones",
      icon: (
        <Badge
          badgeContent={notifications?.unread_total || 0}
          color="warning"
          max={99}
          overlap="circular"
          sx={{
            "& .MuiBadge-badge": {
              fontSize: 10,
              height: 16,
              minWidth: 16,
              borderRadius: MENU_RADIUS,
              bgcolor: menuPalette.badgeBg,
              color: menuPalette.badgeText,
            },
          }}
        >
          <NotificationsNoneRoundedIcon sx={{ fontSize: 20 }} />
        </Badge>
      ),
      onClick: () => {
        if (mobileOpen && panelSection === "notifications") {
          setMobileOpen(false);
          return;
        }
        openNotifications();
      },
      active: mobileOpen && panelSection === "notifications",
      disabled: false,
    },
    {
      key: "menu",
      label: "Menu",
      icon: (
        <Box
          sx={{
            width: 20,
            height: 20,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            color: "inherit",
            "& svg": {
              display: "block",
              height: "18px !important",
              width: "auto",
              fill: "currentColor",
            },
          }}
        >
          <Logo />
        </Box>
      ),
      onClick: () => {
        if (mobileOpen && panelSection === "apps") {
          setMobileOpen(false);
          return;
        }
        setPanelSection("apps");
        openMobileMenu();
      },
      active: mobileOpen && panelSection === "apps",
      disabled: false,
    },
    {
      key: "perfil",
      label: "Perfil",
      icon: <PersonOutlineRoundedIcon sx={{ fontSize: 20 }} />,
      href: quickLinks.perfil,
      onClick: () => navigate(quickLinks.perfil, { closeMobile: true }),
      active: isSamePath(quickLinks.perfil),
      disabled: !quickLinks.perfil || quickLinks.perfil === "#",
    },
  ];
  const withCollapsedHintHandlers = (label) => ({
    onMouseEnter: (event) => {
      if (!desktopOpen) {
        openCollapsedHint(event.currentTarget, label);
      }
    },
    onMouseLeave: () => {
      if (!desktopOpen) {
        closeCollapsedHint();
      }
    },
    onFocus: (event) => {
      if (!desktopOpen) {
        openCollapsedHint(event.currentTarget, label);
      }
    },
    onBlur: () => {
      if (!desktopOpen) {
        closeCollapsedHint();
      }
    },
  });

  const renderModules = (modules = [], parentKey = "", mobile = false) => {
    if (!Array.isArray(modules) || modules.length === 0) {
      return (
        <Typography variant="body2" sx={{ px: 2, py: 1.5, color: "text.secondary" }}>
          Sin modulos disponibles.
        </Typography>
      );
    }

    const moduleKeys = modules.map((_, index) => `${parentKey}:module:${index}`);

    return (
      <List disablePadding sx={{ pb: 0.5 }}>
        {modules.map((module, moduleIndex) => {
          const moduleKey = `${parentKey}:module:${moduleIndex}`;

          if (isSeparator(module)) {
            return <SectionSeparator key={moduleKey} title={getSeparatorTitle(module)} />;
          }
          if (!module || !module.name) {
            return null;
          }

          const isExpandable = hasChildren(module);
          const expanded = Boolean(expandedMap[moduleKey]);
          const moduleColor = mobile ? menuPalette.text : menuPalette.panelText;
          const moduleHoverBg = mobile ? alpha(menuPalette.text, 0.14) : menuPalette.itemHoverBg;
          const childColor = mobile ? alpha(menuPalette.text, 0.9) : alpha(menuPalette.panelText, 0.78);
          const childHoverBg = mobile ? alpha(menuPalette.text, 0.14) : menuPalette.itemHoverBg;

          return (
            <Box key={moduleKey}>
              <ListItemButton
                onClick={() => {
                  if (isExpandable) {
                    toggleExpanded(moduleKey, moduleKeys);
                    return;
                  }
                  navigate(module.url, { closeMobile: mobile });
                }}
                sx={{
                  minHeight: 38,
                  borderRadius: MENU_RADIUS,
                  mx: 1,
                  mb: 0.3,
                  pr: isExpandable ? 1.8 : 1.25,
                  color: moduleColor,
                  "&:hover": {
                    bgcolor: moduleHoverBg,
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 30, color: "inherit" }}>
                  <MenuIcon icon={module.icon} label={module.name} />
                </ListItemIcon>
                <ListItemText
                  primary={module.name}
                  primaryTypographyProps={{ fontSize: 12.8, fontWeight: 500 }}
                />
                {isExpandable ? <ChevronRightRoundedIcon sx={{ fontSize: 17, flexShrink: 0 }} /> : null}
              </ListItemButton>

              {isExpandable ? (
                <Collapse in={expanded} timeout="auto" unmountOnExit>
                  <List disablePadding sx={{ pb: 0.4 }}>
                    {module.children.map((child, childIndex) => {
                      const childKey = `${moduleKey}:child:${childIndex}`;
                      if (isSeparator(child)) {
                        return <SectionSeparator key={childKey} title={getSeparatorTitle(child)} />;
                      }
                      if (!child || !child.name) {
                        return null;
                      }
                      return (
                        <ListItemButton
                          key={childKey}
                          onClick={() => navigate(child.url, { closeMobile: mobile })}
                          sx={{
                            minHeight: 34,
                            borderRadius: MENU_RADIUS,
                            mx: 1,
                            mb: 0.2,
                            pl: 4,
                            color: childColor,
                            "&:hover": {
                              bgcolor: childHoverBg,
                              color: moduleColor,
                            },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 24, color: "inherit" }}>
                            {child.icon ? (
                              <MenuIcon icon={child.icon} label={child.name} />
                            ) : (
                              <Box
                                sx={{
                                  width: 6,
                                  height: 6,
                                  borderRadius: "50%",
                                  bgcolor: alpha(menuPalette.panelText, 0.56),
                                }}
                              />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary={child.name}
                            primaryTypographyProps={{ fontSize: 12.2, fontWeight: 500 }}
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Collapse>
              ) : null}
            </Box>
          );
        })}
      </List>
    );
  };

  const renderApps = ({ mobile = false, collapsed = false } = {}) => {
    const appKeys = menuItems
      .map((entry, index) => (!isSeparator(entry) && entry?.name ? `app:${index}` : null))
      .filter(Boolean);

    return (
      <List disablePadding sx={{ py: 0.6 }}>
        {menuItems.map((item, index) => {
          const appKey = `app:${index}`;

          if (isSeparator(item)) {
            return collapsed ? null : <SectionSeparator key={appKey} title={getSeparatorTitle(item)} />;
          }
          if (!item || !item.name) {
            return null;
          }

          const isExpandable = hasModules(item);
          const expanded = Boolean(expandedMap[appKey]);
          const isActive = activeApp === String(index);
          const listTextColor = collapsed
            ? menuPalette.text
            : (mobile ? menuPalette.text : menuPalette.panelText);

          const hoverBg = collapsed
            ? alpha(menuPalette.text, 0.14)
            : (mobile ? alpha(menuPalette.text, 0.14) : menuPalette.itemHoverBg);
          const activeBg = collapsed
            ? alpha(menuPalette.text, 0.2)
            : (mobile ? alpha(menuPalette.text, 0.18) : menuPalette.itemActiveBg);
          const activeColor = collapsed
            ? menuPalette.text
            : (mobile ? menuPalette.text : menuPalette.itemActiveText);

          return (
            <Box key={appKey}>
              <ListItemButton
                onClick={(event) => {
                  setActiveApp(String(index));
                  if (isExpandable) {
                    if (!mobile && collapsed) {
                      closeCollapsedHint();
                      setCollapsedFlyout({
                        anchorEl: event.currentTarget,
                        item,
                        anchorPosition: getCollapsedFlyoutAnchorPosition(event.currentTarget),
                      });
                      return;
                    }
                    toggleExpanded(appKey, appKeys);
                    return;
                  }
                  navigate(item.url, { closeMobile: mobile });
                }}
                onMouseEnter={(event) => {
                  if (!mobile && collapsed) {
                    openCollapsedHint(event.currentTarget, item.name);
                  }
                }}
                onMouseLeave={() => {
                  if (!mobile && collapsed) {
                    closeCollapsedHint();
                  }
                }}
                onFocus={(event) => {
                  if (!mobile && collapsed) {
                    openCollapsedHint(event.currentTarget, item.name);
                  }
                }}
                onBlur={() => {
                  if (!mobile && collapsed) {
                    closeCollapsedHint();
                  }
                }}
                sx={{
                  minHeight: 44,
                  height: collapsed ? 44 : "auto",
                  borderRadius: MENU_RADIUS,
                  mx: 1,
                  mb: 0.35,
                  justifyContent: collapsed ? "center" : "initial",
                  flexDirection: "row",
                  alignItems: "center",
                  py: collapsed ? 0 : undefined,
                  px: collapsed ? 0.7 : 1.25,
                  pr: collapsed ? 0.7 : 2,
                  color: isActive
                    ? activeColor
                    : listTextColor,
                  bgcolor: isActive ? activeBg : "transparent",
                  "&:hover": {
                    bgcolor: hoverBg,
                  },
                }}
              >
                {collapsed ? (
                  <Box
                    sx={{
                      position: "relative",
                      display: "flex",
                      width: "100%",
                      alignItems: "center",
                      justifyContent: "center",
                      minHeight: 22,
                    }}
                  >
                    <Box sx={{ display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                      <MenuIcon icon={item.icon} label={item.name} />
                    </Box>
                    {isExpandable ? (
                      <Box
                        sx={{
                          position: "absolute",
                          right: 0,
                          top: "50%",
                          transform: "translate(34%, -50%)",
                          fontSize: 12,
                          opacity: 0.95,
                          zIndex: 1,
                        }}
                      >
                        <ChevronRightRoundedIcon fontSize="inherit" />
                      </Box>
                    ) : null}
                  </Box>
                ) : (
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: 1.25,
                      justifyContent: "center",
                      color: "inherit",
                    }}
                  >
                    <MenuIcon icon={item.icon} label={item.name} />
                  </ListItemIcon>
                )}

                {!collapsed ? (
                  <ListItemText
                    primary={item.name}
                    primaryTypographyProps={{ fontSize: 13.2, fontWeight: 600 }}
                  />
                ) : null}

                {!collapsed && isExpandable ? <ChevronRightRoundedIcon sx={{ fontSize: 17, flexShrink: 0 }} /> : null}
              </ListItemButton>

              {!collapsed && isExpandable ? (
                <Collapse in={expanded} timeout="auto" unmountOnExit>
                  {renderModules(item.modules || [], appKey, mobile)}
                </Collapse>
              ) : null}
            </Box>
          );
        })}
      </List>
    );
  };

  const renderNotifications = () => {
    const items = Array.isArray(notifications?.items) ? notifications.items : [];
    const totalCount = Number(notifications?.total || 0);
    const unreadCount = Number(notifications?.unread_total || 0);

    return (
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <Box sx={{ px: 1.5, pt: 1.4, pb: 1.1, borderBottom: "1px solid", borderColor: "divider" }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: menuPalette.panelText }}>
            Notificaciones
          </Typography>
          <Typography variant="caption" sx={{ color: alpha(menuPalette.panelText, 0.72) }}>
            {notifications?.status_text || "Todo al dia"}
          </Typography>
          <Stack direction="row" spacing={0.8} sx={{ mt: 0.7 }}>
            <Button
              size="small"
              variant="outlined"
              disableElevation
              sx={{
                borderRadius: MENU_RADIUS,
                px: 1.1,
                minHeight: 30,
                minWidth: 78,
                textTransform: "none",
                fontWeight: 700,
                fontSize: 12.5,
                color: alpha(menuPalette.panelText, 0.92),
                borderColor: alpha(menuPalette.panelText, 0.28),
                bgcolor: alpha(menuPalette.panelText, 0.08),
                "&:hover": {
                  borderColor: alpha(menuPalette.panelText, 0.46),
                  bgcolor: alpha(menuPalette.panelText, 0.14),
                },
              }}
            >
              {totalCount} total
            </Button>
            {unreadCount > 0 ? (
              <Button
                size="small"
                variant="contained"
                disableElevation
                sx={{
                  borderRadius: MENU_RADIUS,
                  px: 1.1,
                  minHeight: 30,
                  minWidth: 96,
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: 12.5,
                  bgcolor: alpha(menuPalette.panelText, 0.2),
                  color: menuPalette.panelText,
                  "&:hover": {
                    bgcolor: alpha(menuPalette.panelText, 0.28),
                  },
                }}
              >
                {unreadCount} pendientes
              </Button>
            ) : null}
          </Stack>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", p: 1 }}>
          {items.length === 0 ? (
            <Paper
              variant="outlined"
              sx={{
                borderRadius: MENU_RADIUS,
                borderStyle: "dashed",
                p: 2,
                textAlign: "center",
                color: alpha(menuPalette.panelText, 0.78),
                borderColor: alpha(menuPalette.panelText, 0.28),
                bgcolor: alpha(menuPalette.panelText, 0.04),
              }}
            >
              <Typography variant="body2">No tienes notificaciones pendientes.</Typography>
            </Paper>
          ) : (
            <Stack spacing={0.8}>
              {items.map((item, index) => {
                const itemKey = item.id || item.ID || `notification-${index}`;
                const isUnread = !normalizeBool(item.is_read ?? item.IsRead);
                return (
                  <Button
                    key={itemKey}
                    onClick={() => openNotificationItem(item)}
                    sx={{
                      width: "100%",
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "flex-start",
                      gap: 1,
                      p: 1.05,
                      minHeight: 66,
                      borderRadius: MENU_RADIUS,
                      border: "1px solid",
                      borderColor: isUnread ? alpha(theme.palette.info.main, 0.44) : alpha(menuPalette.panelText, 0.2),
                      bgcolor: isUnread ? alpha(theme.palette.info.main, 0.14) : alpha(menuPalette.panelText, 0.06),
                      color: menuPalette.panelText,
                      textTransform: "none",
                      textAlign: "left",
                      "&:hover": {
                        bgcolor: alpha(menuPalette.panelText, 0.12),
                        borderColor: isUnread ? alpha(theme.palette.info.main, 0.56) : alpha(menuPalette.panelText, 0.34),
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 30,
                        height: 30,
                        borderRadius: MENU_RADIUS,
                        bgcolor: alpha(menuPalette.panelText, 0.14),
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        overflow: "hidden",
                        lineHeight: 0,
                        "& svg, & img, & i": {
                          width: 18,
                          height: 18,
                          maxWidth: "100%",
                          maxHeight: "100%",
                          fontSize: 18,
                        },
                      }}
                    >
                      <MenuIcon
                        icon={readNotificationIcon(item)}
                        label={item.object_type || item.ObjectType || "N"}
                      />
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 700,
                          lineHeight: 1.25,
                          color: menuPalette.panelText,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          wordBreak: "break-word",
                        }}
                      >
                        {item.preview || item.Preview || "Notificacion"}
                      </Typography>
                      <Stack direction="row" spacing={0.8} sx={{ mt: 0.45, alignItems: "center", justifyContent: "space-between" }}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: alpha(menuPalette.panelText, 0.75),
                            minWidth: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item.created_at || item.CreatedAt || ""}
                        </Typography>
                        {isUnread ? (
                          <Box
                            component="span"
                            sx={{
                              px: 0.65,
                              py: 0.14,
                              fontSize: 10,
                              fontWeight: 700,
                              lineHeight: 1.05,
                              whiteSpace: "nowrap",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                              borderRadius: MENU_RADIUS,
                              bgcolor: "info.main",
                              color: "info.contrastText",
                            }}
                          >
                            Nuevo
                          </Box>
                        ) : null}
                      </Stack>
                    </Box>
                  </Button>
                );
              })}
            </Stack>
          )}
        </Box>
      </Box>
    );
  };

  const renderDesktopDrawer = () => (
    <DesktopDrawer
      variant="permanent"
      open={desktopOpen}
      sx={{
        zIndex: 120,
        "& .MuiDrawer-paper": {
          position: "fixed",
          left: 0,
          top: 0,
          bottom: 0,
          height: "100vh",
          bgcolor: desktopOpen ? menuPalette.panelBg : menuPalette.bg,
          color: desktopOpen ? menuPalette.panelText : menuPalette.text,
          borderRight: "1px solid",
          borderColor: menuPalette.border,
          borderRadius: MENU_RADIUS,
          boxShadow: "none",
        },
      }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            minHeight: 64,
            px: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: desktopOpen ? "space-between" : "center",
            gap: 0.5,
            bgcolor: desktopOpen ? menuPalette.panelBg : menuPalette.bg,
          }}
        >
          {desktopOpen ? (
            <Button
              onClick={() => navigate(firstMenuLink, { closeMobile: false })}
              sx={{ minWidth: 0, p: 0, color: "inherit", borderRadius: MENU_RADIUS }}
            >
              <Logo />
            </Button>
          ) : null}
          <Stack direction="row" spacing={0.5}>
            {desktopOpen ? (
              <Tooltip title={modeLabel} placement="bottom">
                <IconButton
                  onClick={toggleMode}
                  sx={modeToggleSx}
                  aria-label={modeLabel}
                >
                  {renderModeIcon(18)}
                </IconButton>
              </Tooltip>
            ) : null}
            {desktopOpen ? (
              <Tooltip title={sectionToggleLabel} placement="bottom">
                <IconButton
                  onClick={togglePanelSection}
                  sx={sectionToggleSx}
                  aria-label={sectionToggleLabel}
                >
                  {renderSectionIcon(18)}
                </IconButton>
              </Tooltip>
            ) : null}
            <IconButton
              onClick={() => setDesktopOpen((prev) => !prev)}
              sx={modeToggleSx}
              aria-label={desktopOpen ? "Colapsar menu" : "Expandir menu"}
            >
              {desktopOpen ? <ChevronLeftRoundedIcon fontSize="small" /> : <MenuRoundedIcon fontSize="small" />}
            </IconButton>
          </Stack>
        </Box>

        <Divider
          sx={{
            borderColor: desktopOpen
              ? alpha(menuPalette.panelText, 0.14)
              : alpha(menuPalette.text, 0.2),
          }}
        />

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            scrollbarGutter: desktopOpen ? "stable" : "auto",
            bgcolor: desktopOpen ? menuPalette.panelBg : menuPalette.bg,
            color: desktopOpen ? menuPalette.panelText : menuPalette.text,
          }}
        >
          {desktopOpen && panelSection === "notifications"
            ? renderNotifications()
            : renderApps({ mobile: false, collapsed: !desktopOpen })}
        </Box>

        <Divider
          sx={{
            borderColor: desktopOpen
              ? alpha(menuPalette.panelText, 0.14)
              : alpha(menuPalette.text, 0.2),
          }}
        />

        <List disablePadding sx={{ py: 0.5, bgcolor: desktopOpen ? menuPalette.panelBg : menuPalette.bg }}>
          {!desktopOpen ? (
            <ListItemButton
              onClick={openNotifications}
              sx={{
                minHeight: 42,
                borderRadius: MENU_RADIUS,
                mx: 1,
                mb: 0.3,
                justifyContent: "center",
                px: 1,
                color: "inherit",
              }}
              {...withCollapsedHintHandlers("Notificaciones")}
            >
              <ListItemIcon sx={{ minWidth: 0, mr: 0, justifyContent: "center", color: "inherit" }}>
                <Badge
                  badgeContent={notifications?.unread_total || 0}
                  color="warning"
                  max={99}
                  overlap="circular"
                  sx={{
                    "& .MuiBadge-badge": {
                      fontSize: 10,
                      height: 16,
                      minWidth: 16,
                      borderRadius: MENU_RADIUS,
                      bgcolor: menuPalette.badgeBg,
                      color: menuPalette.badgeText,
                    },
                  }}
                >
                  <NotificationsNoneRoundedIcon sx={{ fontSize: 20 }} />
                </Badge>
              </ListItemIcon>
            </ListItemButton>
          ) : null}

          {!desktopOpen ? (
            <ListItemButton
              onClick={toggleMode}
              sx={{
                minHeight: 42,
                borderRadius: MENU_RADIUS,
                mx: 1,
                mb: 0.3,
                justifyContent: "center",
                px: 1,
                color: "inherit",
              }}
              {...withCollapsedHintHandlers(modeLabel)}
            >
              <ListItemIcon sx={{ minWidth: 0, mr: 0, justifyContent: "center", color: "inherit" }}>
                {renderModeIcon(18)}
              </ListItemIcon>
            </ListItemButton>
          ) : null}

          <ListItemButton
            onClick={openUserMenu}
            sx={{
              minHeight: 42,
              borderRadius: MENU_RADIUS,
              mx: 1,
              justifyContent: desktopOpen ? "initial" : "center",
              px: desktopOpen ? 1.25 : 1,
              color: "inherit",
            }}
            {...(!desktopOpen ? withCollapsedHintHandlers("Perfil") : {})}
          >
            <ListItemIcon sx={{ minWidth: 0, mr: desktopOpen ? 1.1 : 0, justifyContent: "center", color: "inherit" }}>
              <Avatar
                sx={{
                  width: 28,
                  height: 28,
                  fontSize: 13,
                  bgcolor: alpha("#ffffff", isDarkMode ? 0.12 : 0.16),
                  color: menuPalette.panelText,
                  border: "1px solid",
                  borderColor: alpha("#ffffff", isDarkMode ? 0.22 : 0.3),
                }}
              >
                {payload?.userAvatarLetter || "U"}
              </Avatar>
            </ListItemIcon>
            {desktopOpen ? (
              <ListItemText
                primary={payload?.username || "Usuario"}
                primaryTypographyProps={{ fontSize: 12.6, fontWeight: 600, noWrap: true }}
              />
            ) : null}
          </ListItemButton>
        </List>
      </Box>
    </DesktopDrawer>
  );

  const renderMobileTopBar = () => (
    <Box
      sx={{
        position: "fixed",
        left: 0,
        right: 0,
        top: 0,
        height: MOBILE_TOP_BAR_HEIGHT,
        px: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        bgcolor: menuPalette.bg,
        color: menuPalette.text,
        borderBottom: "1px solid",
        borderColor: menuPalette.border,
        boxShadow: "0 8px 16px rgba(0,0,0,0.16)",
        zIndex: MOBILE_CHROME_Z_INDEX,
        pointerEvents: "auto",
      }}
    >
      <Button
        onClick={() => navigate(firstMenuLink, { closeMobile: false })}
        sx={{ minWidth: 0, p: 0, color: "inherit", borderRadius: MENU_RADIUS }}
      >
        <Logo />
      </Button>

      <Stack direction="row" spacing={0.75}>
        <Tooltip title={sectionToggleLabel} placement="bottom">
          <IconButton
            onClick={() => {
              if (mobileOpen && panelSection === "notifications") {
                setMobileOpen(false);
                return;
              }
              openNotifications();
            }}
            sx={{
              width: 34,
              height: 34,
              borderRadius: MENU_RADIUS,
              color: "inherit",
              border: "1px solid",
              borderColor: alpha(menuPalette.text, 0.28),
            }}
            aria-label={sectionToggleLabel}
          >
            {renderSectionIcon(18)}
          </IconButton>
        </Tooltip>
        <IconButton
          onClick={toggleMode}
          sx={{
            width: 34,
            height: 34,
            borderRadius: MENU_RADIUS,
            color: "inherit",
            border: "1px solid",
            borderColor: alpha(menuPalette.text, 0.28),
          }}
          aria-label={modeLabel}
        >
          {renderModeIcon(18)}
        </IconButton>
      </Stack>
    </Box>
  );

  const renderMobileBottomNav = () => (
    <BottomNavigation
      showLabels
      value={(() => {
        if (mobileOpen && panelSection === "notifications") return "notifications";
        if (mobileOpen) return "menu";
        if (isSamePath(quickLinks.perfil)) return "perfil";
        return null;
      })()}
      sx={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        height: MOBILE_BOTTOM_BAR_HEIGHT,
        maxWidth: "100vw",
        bgcolor: menuPalette.bg,
        color: menuPalette.text,
        borderTop: "1px solid",
        borderColor: menuPalette.border,
        boxShadow: "0 -8px 18px rgba(0,0,0,0.18)",
        zIndex: MOBILE_CHROME_Z_INDEX,
        pointerEvents: "auto",
        "& .MuiBottomNavigationAction-root": {
          minWidth: 0,
          maxWidth: "none",
          borderTop: "2px solid transparent",
          color: alpha(menuPalette.text, 0.9),
          "&.Mui-selected": {
            color: "#ffffff",
            borderTopColor: alpha(menuPalette.text, 0.95),
            bgcolor: alpha(menuPalette.text, 0.14),
          },
        },
        "& .MuiBottomNavigationAction-label": {
          fontSize: 10.5,
          lineHeight: 1.15,
          "&.Mui-selected": {
            fontSize: 10.5,
            fontWeight: 700,
          },
        },
      }}
    >
      {quickActionItems.map((item) => (
        <BottomNavigationAction
          key={item.key}
          value={item.key}
          label={item.label}
          icon={item.icon}
          onClick={item.onClick}
          disabled={item.disabled}
        />
      ))}
    </BottomNavigation>
  );

  const renderMobileDrawer = () => (
    <MuiDrawer
      variant="persistent"
      anchor="bottom"
      open={mobileOpen}
      onClose={() => setMobileOpen(false)}
      sx={{
        pointerEvents: "none",
        zIndex: MOBILE_DRAWER_Z_INDEX,
        "& .MuiDrawer-paper": {
          pointerEvents: "auto",
          maxWidth: "100vw",
          height: `calc((100dvh - ${MOBILE_TOP_BAR_HEIGHT + MOBILE_BOTTOM_BAR_HEIGHT}px) * 0.75)`,
          maxHeight: `calc((100dvh - ${MOBILE_TOP_BAR_HEIGHT + MOBILE_BOTTOM_BAR_HEIGHT}px) * 0.75)`,
          borderRadius: MENU_RADIUS,
          bgcolor: menuPalette.bg,
          color: menuPalette.text,
          borderTop: "1px solid",
          borderColor: menuPalette.border,
          overflow: "hidden",
          mb: `${MOBILE_BOTTOM_BAR_HEIGHT}px`,
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Box
          sx={{
            px: 1,
            py: 1,
            borderBottom: "1px solid",
            borderColor: menuPalette.border,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Logo />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: menuPalette.text }} noWrap>
                {payload?.username || "Usuario"}
              </Typography>
              <Typography variant="caption" sx={{ color: alpha(menuPalette.text, 0.78) }} noWrap>
                Cuenta de usuario
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={0.75}>
            <IconButton
              onClick={toggleMode}
              sx={{
                width: 34,
                height: 34,
                borderRadius: MENU_RADIUS,
                color: "inherit",
                border: "1px solid",
                borderColor: alpha(menuPalette.panelText, 0.26),
              }}
              aria-label={modeLabel}
            >
              {renderModeIcon(18)}
            </IconButton>
          </Stack>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", scrollbarGutter: "stable", bgcolor: menuPalette.bg, color: menuPalette.text }}>
          {panelSection === "notifications" ? (
            renderNotifications()
          ) : (
            <Box>
              <ListSubheader
                component="div"
                disableSticky
                sx={{
                  bgcolor: "transparent",
                  color: alpha(menuPalette.panelText, 0.86),
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  letterSpacing: 0.45,
                  textTransform: "uppercase",
                  lineHeight: 1.2,
                  py: 1,
                }}
              >
                Principal
              </ListSubheader>
              {renderApps({ mobile: true, collapsed: false })}
              <ListSubheader
                component="div"
                disableSticky
                sx={{
                  bgcolor: "transparent",
                  color: alpha(menuPalette.panelText, 0.86),
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  letterSpacing: 0.45,
                  textTransform: "uppercase",
                  lineHeight: 1.2,
                  py: 1,
                }}
              >
                Otros
              </ListSubheader>
              <List disablePadding sx={{ pb: 1 }}>
                <ListItemButton
                  onClick={openNotifications}
                  sx={{
                    minHeight: 42,
                    mx: 1,
                    borderRadius: MENU_RADIUS,
                    color: menuPalette.text,
                    "&:hover": { bgcolor: alpha(menuPalette.text, 0.14) },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 32, color: "inherit" }}>
                    <Badge
                      badgeContent={notifications?.unread_total || 0}
                      color="warning"
                      max={99}
                      overlap="circular"
                      sx={{
                        "& .MuiBadge-badge": {
                          fontSize: 10,
                          height: 16,
                          minWidth: 16,
                          borderRadius: MENU_RADIUS,
                          bgcolor: menuPalette.badgeBg,
                          color: menuPalette.badgeText,
                        },
                      }}
                    >
                      <NotificationsNoneRoundedIcon sx={{ fontSize: 20 }} />
                    </Badge>
                  </ListItemIcon>
                  <ListItemText
                    primary="Notificaciones"
                    primaryTypographyProps={{ fontSize: 13.2, fontWeight: 600 }}
                  />
                </ListItemButton>
                <ListItemButton
                  onClick={() => navigate(payload?.perfilUrl || "#", { closeMobile: true })}
                  sx={{
                    minHeight: 42,
                    mx: 1,
                    borderRadius: MENU_RADIUS,
                    color: menuPalette.text,
                    "&:hover": { bgcolor: alpha(menuPalette.text, 0.14) },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 32, color: "inherit" }}>
                    <PersonOutlineRoundedIcon sx={{ fontSize: 20 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Perfil"
                    primaryTypographyProps={{ fontSize: 13.2, fontWeight: 600 }}
                  />
                </ListItemButton>
                <ListItemButton
                  onClick={() => navigate(payload?.logoutUrl || "/logout", { closeMobile: true })}
                  sx={{
                    minHeight: 42,
                    mx: 1,
                    borderRadius: MENU_RADIUS,
                    color: menuPalette.text,
                    "&:hover": { bgcolor: alpha(menuPalette.text, 0.14) },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 32, color: "inherit" }}>
                    <LogoutRoundedIcon sx={{ fontSize: 20 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Cerrar sesion"
                    primaryTypographyProps={{ fontSize: 13.2, fontWeight: 600 }}
                  />
                </ListItemButton>
              </List>
            </Box>
          )}
        </Box>
      </Box>
    </MuiDrawer>
  );

  return (
      <Box ref={shellRootRef} sx={{ position: "relative", zIndex: 120, pointerEvents: "auto" }}>
      {isMobile ? null : renderDesktopDrawer()}
      {isMobile ? renderMobileBottomNav() : null}
      {isMobile ? renderMobileDrawer() : null}
      {!isMobile && !desktopOpen && Boolean(collapsedHint.position && collapsedHint.label) && !Boolean(collapsedFlyout.item) ? (
        <Box
          sx={{
            position: "fixed",
            top: collapsedHint.position?.top || 0,
            left: collapsedHint.position?.left || 0,
            transform: "translateY(-50%)",
            zIndex: 1600,
            pointerEvents: "none",
          }}
        >
          <Paper
            elevation={0}
            sx={{
              px: 1.1,
              py: 0.7,
              minWidth: 150,
              maxWidth: 260,
              borderRadius: MENU_RADIUS,
              bgcolor: menuPalette.panelBg,
              color: menuPalette.panelText,
              border: "1px solid",
              borderColor: menuPalette.border,
              boxShadow: "0 14px 34px rgba(0, 0, 0, 0.34)",
              overflow: "hidden",
            }}
          >
            <Typography sx={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.2, whiteSpace: "nowrap" }}>
              {collapsedHint.label}
            </Typography>
          </Paper>
        </Box>
      ) : null}
      {!isMobile && !desktopOpen ? (
        <Popover
          disablePortal
          open={Boolean(collapsedFlyout.anchorPosition && collapsedFlyout.item)}
          anchorReference="anchorPosition"
          anchorPosition={collapsedFlyout.anchorPosition || undefined}
          onClose={closeCollapsedFlyout}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          PaperProps={{
            sx: {
              ml: 0,
              minWidth: 250,
              maxWidth: 300,
              borderRadius: MENU_RADIUS,
              bgcolor: menuPalette.panelBg,
              color: menuPalette.panelText,
              border: "1px solid",
              borderColor: menuPalette.border,
              boxShadow: "none",
              overflow: "hidden",
            },
          }}
        >
          <Box
            sx={{
              p: 1,
              borderBottom: "1px solid",
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 0.8,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}
              noWrap
            >
              {collapsedFlyout.item?.name || "Menu"}
            </Typography>
            {collapsedFlyout.item?.url ? (
              <IconButton
                size="small"
                onClick={() => navigate(collapsedFlyout.item?.url, { closeMobile: false })}
                aria-label={`Abrir ${collapsedFlyout.item?.name || "menu"}`}
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: MENU_RADIUS,
                  color: alpha(menuPalette.panelText, 0.88),
                  border: "1px solid",
                  borderColor: alpha(menuPalette.panelText, 0.26),
                  "&:hover": {
                    bgcolor: menuPalette.itemHoverBg,
                    color: menuPalette.panelText,
                    borderColor: alpha(menuPalette.panelText, 0.46),
                  },
                }}
              >
                <OpenInNewRoundedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            ) : null}
          </Box>
          <Box sx={{ maxHeight: "68vh", overflowY: "auto", scrollbarGutter: "stable", p: 0.4 }}>
            {renderModules(collapsedFlyout.item?.modules || [], "flyout", false)}
          </Box>
        </Popover>
      ) : null}

      <Menu
        open={Boolean(userAnchor)}
        anchorEl={userAnchor}
        onClose={closeUserMenu}
        disablePortal
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 220,
            borderRadius: MENU_RADIUS,
          },
        }}
      >
        <Box sx={{ px: 1.5, py: 1, maxWidth: 240 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis" }}>
            {payload?.username || "Usuario"}
          </Typography>
        </Box>
        <Divider />
        <MenuItem
          onClick={() => {
            closeUserMenu();
            toggleMode();
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            {renderModeIcon(17)}
            <Typography variant="body2">{isDarkMode ? "Modo claro" : "Modo oscuro"}</Typography>
          </Stack>
        </MenuItem>
        <MenuItem
          onClick={() => {
            closeUserMenu();
            navigate(payload?.perfilUrl || "#", { closeMobile: true });
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <PersonOutlineRoundedIcon sx={{ fontSize: 17 }} />
            <Typography variant="body2">Perfil</Typography>
          </Stack>
        </MenuItem>
        <MenuItem
          onClick={() => {
            closeUserMenu();
            navigate(payload?.logoutUrl || "/logout", { closeMobile: true });
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <LogoutRoundedIcon sx={{ fontSize: 17 }} />
            <Typography variant="body2">Cerrar sesion</Typography>
          </Stack>
        </MenuItem>
      </Menu>

      {dialog.open ? (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 210,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            onClick={closeDialog}
            sx={{
              position: "absolute",
              inset: 0,
              bgcolor: alpha("#000", 0.56),
            }}
          />

          <Paper
            elevation={14}
            sx={{
              position: "relative",
              width: "min(92vw, 900px)",
              height: "90vh",
              borderRadius: MENU_RADIUS,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              zIndex: 211,
            }}
          >
            <Box
              sx={{
                px: 1.5,
                py: 1,
                borderBottom: "1px solid",
                borderColor: "divider",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, pr: 2 }} noWrap>
                {dialog.title || "Notificacion"}
              </Typography>
              <Button onClick={closeDialog} size="small" variant="outlined" sx={{ borderRadius: MENU_RADIUS }}>
                Cerrar
              </Button>
            </Box>
            <Box sx={{ flex: 1, minHeight: 0, bgcolor: "background.default" }}>
              {dialog.iframe ? (
                <Box component="iframe" src={dialog.url} title="Notificacion" sx={{ width: "100%", height: "100%", border: 0 }} />
              ) : (
                <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Button variant="contained" onClick={() => window.open(dialog.url, "_blank", "noopener,noreferrer")}>Abrir notificacion</Button>
                </Box>
              )}
            </Box>
          </Paper>
        </Box>
      ) : null}
    </Box>
  );
}
